// Region lists + compliance helpers for retail checkout gateways.
// Grounded in 2026 research (Canada/CBSA federal, Manitoba Long-Bladed Weapon
// Control Act, US state knife-age laws). This is operational config, NOT legal
// advice — see src/components/LegalPage.tsx.

// Canada-only since Oct 2026 (owner decision: no US sales). US_STATES and the
// provincesFor branch stay for the admin directory, but customers only see Canada.
export const COUNTRIES = ['Canada'] as const;

export const CA_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
];

export const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois',
  'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts',
  'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
  'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
];

export const regionsFor = (country: string): string[] =>
  country === 'United States' ? US_STATES : CA_PROVINCES;

// Minimum purchase age. 18 is the floor everywhere we sell (research 2026).
export const MIN_AGE = 18;

// Whole-years age from a YYYY-MM-DD string. NaN if empty/invalid.
export const computeAge = (dobStr: string): number => {
  if (!dobStr) return NaN;
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return NaN;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age;
};

// Jurisdictions we refuse to ship to. Empty by design — no jurisdiction we
// researched bans the SALE of machetes to adults. Add { country, region }
// entries here to block checkout for that region later.
export const RESTRICTED_REGIONS: { country: string; region: string }[] = [];

export const isRegionRestricted = (country: string, region: string): boolean =>
  RESTRICTED_REGIONS.some((r) => r.country === country && r.region === region);

// Extra legally-required notice for specific regions, shown at checkout.
export const getRegionNotice = (country: string, region: string): string | null => {
  if (country === 'Canada' && region === 'Manitoba') {
    return "Manitoba's Long-Bladed Weapon Control Act applies: buyers must be 18+, present valid photo ID on delivery, and this sale is recorded (name, address, date of birth, item, and date) and kept for two years as required by provincial law.";
  }
  return null;
};

// Always-shown buyer-responsibility notice at checkout.
export const LOCAL_LAW_NOTICE =
  'Machetes are sold as tools for lawful use. Local possession and carry rules vary — some cities (for example, New York City) restrict carrying blades in public. You are responsible for following the laws where you live.';
